/**
 * Reader reads (web.md §3): the list as a TanStack infinite query over `messages.list` (page 100), the
 * entity lookups rows and Details need (sources, topics, filters, tags), and the corrections scope.
 * Every key comes from engine-client/keys.ts so engine events reach them.
 */

import { keepPreviousData, useInfiniteQuery, useQueries, type InfiniteData } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  toDigesterError,
  type Correction,
  type FilterRule,
  type Folder,
  type ListQuery,
  type MessageDetail,
  type MessageId,
  type MessagePage,
  type MessageRow,
  type Model,
  type RuleId,
  type ScoringRule,
  type Source,
  type Tag,
  type Topic,
} from '@digester/core';
import { useEngine, useEngineQuery } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { useFolders } from '../app/hooks.js';
import type { ReaderScope } from '../app/reader-route.js';
import type { ListFilter } from '../app/ui-store.js';
import { entryOf, modelName, shortModelName, useCatalog, useModels } from '../settings/models/data.js';

export const PAGE_SIZE = 100;

export interface Lookups {
  /** The user's scoring models in model order (models.md §2). */
  models: Model[];
  folders: Folder[];
  sources: Source[];
  topics: Topic[];
  filters: FilterRule[];
  scoringRules: ScoringRule[];
  tags: Tag[];
  folder(id: string | null | undefined): Folder | undefined;
  source(id: string | null | undefined): Source | undefined;
  topic(id: string | null | undefined): Topic | undefined;
  rule(id: string | null | undefined): FilterRule | ScoringRule | undefined;
  tag(id: string | null | undefined): Tag | undefined;
  model(id: string | null | undefined): Model | undefined;
  /** "Jev 1.13" / "Multilingual E5 Small" ('' for no id). */
  modelName(id: string | null | undefined): string;
  /** "Jev" / "On-device" — for score lines and tooltips. */
  modelShort(id: string | null | undefined): string;
  /** True for an on-device (embedding) model. */
  onDevice(id: string | null | undefined): boolean;
}

function byId<T extends { id: string }>(list: readonly T[]): Map<string, T> {
  return new Map(list.map((x) => [x.id, x]));
}

/** Entity lists the reader shows by name (archived ones included, so old history still resolves). */
export function useLookups(): Lookups {
  const folders = useFolders();
  const sources = useEngineQuery(qk.sources({ includeArchived: true }), (e) => e.sources.list({ includeArchived: true })).data;
  const topics = useEngineQuery(qk.topics({ includeArchived: true }), (e) => e.topics.list({ includeArchived: true })).data;
  const filters = useEngineQuery(qk.rules('filter'), (e) => e.rules.list('filter')).data;
  const scoring = useEngineQuery(qk.rules('scoring'), (e) => e.rules.list('scoring')).data;
  const tags = useEngineQuery(qk.tags(), (e) => e.tags.list()).data;
  const { models } = useModels();
  const catalog = useCatalog();
  return useMemo(() => {
    const m = byId(models);
    const f = byId(folders);
    const s = byId(sources ?? []);
    const t = byId(topics ?? []);
    const r = new Map<string, FilterRule | ScoringRule>([...(filters ?? []), ...(scoring ?? [])].map((x) => [x.id, x]));
    const g = byId(tags ?? []);
    return {
      models,
      folders,
      sources: sources ?? [],
      topics: topics ?? [],
      filters: filters ?? [],
      scoringRules: scoring ?? [],
      tags: tags ?? [],
      folder: (id) => (id ? f.get(id) : undefined),
      source: (id) => (id ? s.get(id) : undefined),
      topic: (id) => (id ? t.get(id) : undefined),
      rule: (id) => (id ? r.get(id) : undefined),
      tag: (id) => (id ? g.get(id) : undefined),
      model: (id) => (id ? m.get(id) : undefined),
      modelName: (id) => modelName(id, models, catalog),
      modelShort: (id) => shortModelName(id, models, catalog),
      onDevice: (id) => (id ? (m.get(id)?.kind ?? entryOf(catalog, id)?.kind) === 'onnx-embedding' : false),
    };
  }, [folders, sources, topics, filters, scoring, tags, models, catalog]);
}

/** The list query for a scope, the search text and the temporary list filters. */
export function buildListQuery(scope: Exclude<ReaderScope, { kind: 'corrections' }>, search: string, filter: ListFilter, pinId: MessageId | null): Omit<ListQuery, 'cursor'> {
  const text = search.trim();
  const base: ListQuery['scope'] = text && filter.allFolders ? { kind: 'all' } : scope.kind === 'folder' ? { kind: 'folder', folderId: scope.folderId } : { kind: 'view', viewId: scope.viewId };
  return {
    scope: base,
    sort: filter.sort,
    limit: PAGE_SIZE,
    ...(text ? { search: text } : {}),
    ...(filter.unreadOnly ? { unreadOnly: true } : {}),
    ...(filter.sourceId ? { sourceId: filter.sourceId } : {}),
    ...(filter.topicId ? { topicId: filter.topicId } : {}),
    // The message being read stays in an unread-only list after it is marked read (the prototype's rule).
    ...(filter.unreadOnly && pinId ? { pinId } : {}),
  };
}

export interface ReaderList {
  rows: MessageRow[];
  total: number;
  unread: number;
  unreadMinutes: number;
  loading: boolean;
  error: Error | null;
  hasNextPage: boolean;
  fetchingNext: boolean;
  fetchNextPage(): void;
}

const scopeOf = (key: readonly unknown[]): string => JSON.stringify((key[1] as { scope?: unknown } | undefined)?.scope ?? null);

/** `messages.list` pages; rows de-duplicated by id (a refetched first page can overlap the next). */
export function useMessageList(query: Omit<ListQuery, 'cursor'>, enabled = true): ReaderList {
  const engine = useEngine();
  const q = useInfiniteQuery<MessagePage, Error, InfiniteData<MessagePage>, readonly unknown[], string | null>({
    queryKey: qk.list(query),
    queryFn: ({ pageParam }) => engine.messages.list(pageParam ? { ...query, cursor: pageParam } : query),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    // Keep the rows on screen while only the search, filters or pin change (not when the scope does).
    placeholderData: (previous, previousQuery) => (previousQuery && scopeOf(previousQuery.queryKey) === scopeOf(qk.list(query)) ? keepPreviousData(previous) : undefined),
  });
  const pages = q.data?.pages ?? [];
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: MessageRow[] = [];
    for (const page of q.data?.pages ?? []) {
      for (const row of page.rows) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
        out.push(row);
      }
    }
    return out;
  }, [q.data]);
  const first = pages[0];
  return {
    rows,
    total: first?.total ?? 0,
    unread: first?.unread ?? 0,
    unreadMinutes: first?.unreadMinutes ?? 0,
    loading: q.isLoading,
    error: q.error ? toDigesterError(q.error) : null,
    hasNextPage: q.hasNextPage,
    fetchingNext: q.isFetchingNextPage,
    fetchNextPage: () => {
      if (q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
    },
  };
}

/** A list row rebuilt from a full message (the Corrections scope lists messages by correction). */
export function rowFromDetail(detail: MessageDetail, filterOrder: readonly FilterRule[]): MessageRow {
  const m = detail.message;
  const position = new Map(filterOrder.map((r) => [r.id, r.position]));
  const matched = Object.entries(m.filterResults)
    .filter(([, v]) => v === 1)
    .map(([id]) => id as RuleId)
    .sort((a, b) => (position.get(a) ?? 1e9) - (position.get(b) ?? 1e9));
  return {
    id: m.id,
    sourceId: m.sourceId,
    folderId: m.placement.folderId,
    title: m.title,
    excerpt: m.excerpt,
    imageUrl: m.imageUrl,
    author: m.author,
    sortAt: m.sortAt,
    read: m.readAt !== null,
    readingMinutes: m.readingMinutes,
    topics: m.topics,
    matchedFilterIds: matched,
    tagIds: m.tagIds,
    scoreState: m.scoreState,
    bestScore: m.bestScore,
    scoredBy: m.scoringModel as MessageRow['scoredBy'],
  };
}

/** A row for a correction whose message is gone (stubs are not readable): built from the snapshot. */
function rowFromSnapshot(c: Correction): MessageRow {
  return {
    id: c.messageId,
    sourceId: c.snapshot.message.sourceId,
    folderId: c.toFolderId ?? ('inbox' as MessageRow['folderId']),
    title: c.snapshot.message.title,
    excerpt: c.snapshot.message.excerpt,
    imageUrl: null,
    author: null,
    sortAt: c.createdAt,
    read: true,
    readingMinutes: 0,
    topics: [],
    matchedFilterIds: [],
    tagIds: [],
    scoreState: 'none',
    bestScore: null,
    scoredBy: null,
  };
}

export interface CorrectionsList extends ReaderList {
  corrections: Correction[];
  /** Messages that no longer exist (shown from the snapshot, not openable). */
  gone: Set<string>;
}

/** The Corrections scope: open corrections, newest first, one row per message. */
export function useCorrectionsList(enabled: boolean, filters: readonly FilterRule[], search: string): CorrectionsList {
  const engine = useEngine();
  const listQuery = { status: 'open' as const, limit: 200 };
  const q = useEngineQuery(qk.corrections(listQuery), (e) => e.corrections.list(listQuery), { enabled });
  const items = q.data?.items ?? [];
  const messageIds = useMemo(() => [...new Set(items.map((c) => c.messageId))], [items]);
  const details = useQueries({
    queries: messageIds.map((id) => ({
      queryKey: qk.message(id),
      queryFn: () => engine.messages.get(id),
      enabled,
    })),
  });
  const gone = new Set<string>();
  const rows: MessageRow[] = [];
  const text = search.trim().toLowerCase();
  messageIds.forEach((id, i) => {
    const d = details[i];
    const correction = items.find((c) => c.messageId === id);
    let row: MessageRow | null = null;
    if (d?.data) row = rowFromDetail(d.data, filters);
    else if (d?.error && correction) {
      gone.add(id);
      row = rowFromSnapshot(correction);
    }
    if (!row) return;
    if (text && !`${row.title ?? ''} ${row.excerpt} ${row.author ?? ''}`.toLowerCase().includes(text)) return;
    rows.push(row);
  });
  const unread = rows.filter((r) => !r.read);
  return {
    corrections: items,
    gone,
    rows,
    total: text ? rows.length : q.data?.total ?? 0,
    unread: unread.length,
    unreadMinutes: unread.reduce((n, r) => n + r.readingMinutes, 0),
    loading: q.isLoading || details.some((d) => d.isLoading),
    error: q.error ? toDigesterError(q.error) : null,
    hasNextPage: false,
    fetchingNext: false,
    fetchNextPage: () => undefined,
  };
}
